import { z } from 'zod';
import { addressSchema, emailSchema, phoneSchema, uuidSchema } from './common.js';
/**
 * Checkout request schema.
 *
 * Note what is deliberately absent: prices, line totals, discount amounts and
 * the grand total. The client may only state WHAT it wants to buy; the server
 * decides what it costs. An `expectedTotal` may be supplied purely so the API
 * can detect a stale cart and refuse rather than silently charge a different
 * amount.
 */
export const checkoutItemSchema = z.object({
    productId: uuidSchema,
    variantId: uuidSchema.nullable().optional(),
    quantity: z.number().int().min(1, 'Quantity must be at least 1').max(999),
});
export const checkoutCustomerSchema = z.object({
    name: z.string().trim().min(2, 'Enter your full name').max(120),
    email: emailSchema,
    phone: phoneSchema,
});
export const createCheckoutSchema = z.object({
    items: z.array(checkoutItemSchema).min(1, 'Your cart is empty').max(50),
    customer: checkoutCustomerSchema,
    shippingAddress: addressSchema.nullable().optional(),
    /** Omitted means "same as shipping". */
    billingAddress: addressSchema.nullable().optional(),
    shippingMethodId: uuidSchema.nullable().optional(),
    couponCode: z.string().trim().toUpperCase().max(50).nullable().optional(),
    salesPageId: uuidSchema.nullable().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    /** Which of the store's payment methods the shopper chose; omitted = the default. */
    paymentMethod: z.string().trim().max(40).optional(),
    /**
     * Echoed back from a price preview. When present and it disagrees with the
     * server's recalculation, the checkout is rejected with TOTAL_MISMATCH.
     */
    expectedTotal: z
        .string()
        .regex(/^\d+(\.\d{1,2})?$/)
        .nullable()
        .optional(),
    /**
     * Mock-gateway control used in development and tests to force an outcome.
     * Ignored by real providers.
     */
    simulate: z.enum(['success', 'failure']).optional(),
});
/** Dry-run pricing used by the storefront cart before submitting. */
export const previewCheckoutSchema = z.object({
    items: z.array(checkoutItemSchema).min(1).max(50),
    shippingMethodId: uuidSchema.nullable().optional(),
    couponCode: z.string().trim().toUpperCase().max(50).nullable().optional(),
    /** Needed to resolve the shipping rate before an address is entered. */
    shippingState: z.string().trim().max(60).nullable().optional(),
    customerEmail: emailSchema.nullable().optional(),
});
export const validateCouponSchema = z.object({
    code: z.string().trim().toUpperCase().min(1, 'Enter a coupon code').max(50),
    items: z.array(checkoutItemSchema).min(1).max(50),
    customerEmail: emailSchema.nullable().optional(),
});
//# sourceMappingURL=checkout.js.map